/**
 * Custom hook to create address form actions
 * Converts individual setters to AddressFormActions
 */

import { useCallback } from 'react'
import type { AddressFormActions } from '../types/address'
import type { UseAddressFormActionsProps } from '../types/hooks'

/**
 * Convert individual setters to AddressFormActions
 */
export function useAddressFormActions(props: UseAddressFormActionsProps): AddressFormActions {
  /** End date is shared across rent/own/family slots — clear all so sync/type switch cannot resurrect a value */
  const clearSharedEndDates = useCallback(() => {
    props.setLeaseEndDate('')
    props.setPlannedMoveOutDate('')
    props.setPlannedMoveOutDateFamily('')
  }, [props.setLeaseEndDate, props.setPlannedMoveOutDate, props.setPlannedMoveOutDateFamily])

  /** One "still here" concept: keep rent/own/family flags aligned for whichever residence tab is active */
  const syncAllStillHere = useCallback((value: boolean) => {
    props.setStillHereRent(value)
    props.setStillHereOwn(value)
    props.setStillHereFamily(value)
  }, [props.setStillHereRent, props.setStillHereOwn, props.setStillHereFamily])

  const handleStillHereRent = useCallback((value: boolean) => {
    syncAllStillHere(value)
    if (value) {
      clearSharedEndDates()
    }
  }, [syncAllStillHere, clearSharedEndDates])

  const handleStillHereOwn = useCallback((value: boolean) => {
    syncAllStillHere(value)
    if (value) {
      clearSharedEndDates()
    }
  }, [syncAllStillHere, clearSharedEndDates])

  const handleStillHereFamily = useCallback((value: boolean) => {
    syncAllStillHere(value)
    if (value) {
      clearSharedEndDates()
    }
  }, [syncAllStillHere, clearSharedEndDates])

  return {
    setAddress: props.setAddress,
    setStreetNumber: props.setStreetNumber,
    setStreetName: props.setStreetName,
    setStreetType: props.setStreetType,
    setAptUnit: props.setAptUnit,
    setCity: props.setCity,
    setState: props.setState,
    setZipCode: props.setZipCode,
    setCountry: props.setCountry,
    setOutsideUS: props.setOutsideUS,
    setResidenceType: props.setResidenceType,
    setStillHereRent: handleStillHereRent,
    setMoveInDateRent: props.setMoveInDateRent,
    setLeaseEndDate: props.setLeaseEndDate,
    setLandlordName: props.setLandlordName,
    setLandlordEmail: props.setLandlordEmail,
    setLandlordMobile: props.setLandlordMobile,
    setRentAmount: props.setRentAmount,
    setMonthsPaidRent: props.setMonthsPaidRent,
    setStillHereOwn: handleStillHereOwn,
    setMoveInDateOwn: props.setMoveInDateOwn,
    setPlannedMoveOutDate: props.setPlannedMoveOutDate,
    setMonthlyMortgagePayment: props.setMonthlyMortgagePayment,
    setStillHereFamily: handleStillHereFamily,
    setMoveInDateFamily: props.setMoveInDateFamily,
    setPlannedMoveOutDateFamily: props.setPlannedMoveOutDateFamily,
    setContactName: props.setContactName,
    setContactEmail: props.setContactEmail,
    setContactMobile: props.setContactMobile,
    setContactWebsite: props.setContactWebsite,
    setContactRelationship: props.setContactRelationship,
    setRentAmountFamily: props.setRentAmountFamily,
    setMonthsResided: props.setMonthsResided,
  }
}

